import {
  COMMA,
  ENTER,
} from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common'
import { SystemFieldIds } from '@galaxy/crm/services/field-service';
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  Signal,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import {
  MatChipInputEvent,
  MatChipsModule,
} from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  FieldValueInterface,
  FormInputComponent,
  InlineEditService,
  ObjectType,
  SimplifiedUser,
} from '@galaxy/crm/static';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { of } from 'rxjs';
import {
  CRMUserService,
  userFilterFn,
} from '../../user.service';
import {
  catchError,
  map,
  switchMap,
  take,
} from 'rxjs/operators';

const NO_VALUE_FOR_TEAM_MEMBER = '<NO_VALUE_FOR_TEAM_MEMBER>';

@Component({
  templateUrl: './additional-team-member-form-input.component.html',
  providers: [],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    GalaxyBadgeModule,
    GalaxyTooltipModule,
  ],
  styleUrls: ['../shared.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AdditionalTeamMemberFormInputComponent extends FormInputComponent {
  @ViewChild('teamMemberInput') teamMemberInput: ElementRef<HTMLInputElement>;

  NO_VALUE_FOR_TEAM_MEMBER = NO_VALUE_FOR_TEAM_MEMBER;

  readOnly = false;
  objectType: ObjectType = 'Contact';
  _control: FormControl<string[]> = new FormControl<string[]>([], {
    nonNullable: true,
  });
  searchControl: FormControl<SimplifiedUser | string> = new FormControl<SimplifiedUser | string>('', {
    nonNullable: true,
  });
  separatorKeysCodes: number[] = [ENTER, COMMA];
  _label = '';
  searchTerm = signal<SimplifiedUser | string>('');
  selectedTeamMemberIDs = signal<string[]>([]);
  selectedTeamMembers = signal<SimplifiedUser[]>([]);
  private _group: UntypedFormGroup;

  set control(control: UntypedFormControl) {
    this._control = control as FormControl<string[]>;
    this._control.markAsPristine();
    this._control.setValue(this._control.value || []);
    this.selectedTeamMemberIDs.set(this._control.value);
    this.userService
      .getMultiUsers(this._control.value)
      .pipe(
        take(1),
        catchError(() => {
          return of([]);
        }),
      )
      .subscribe((sUsr) => {
        this.selectedTeamMembers.set(sUsr);
      });
    this._control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newValue) => {
      this.selectedTeamMemberIDs.set(newValue || []);
      this.userService
        .getMultiUsers(newValue)
        .pipe(
          take(1),
          catchError(() => {
            return of([]);
          }),
        )
        .subscribe((sUsr) => {
          this.selectedTeamMembers.set(sUsr);
        });
    });
  }

  get control(): UntypedFormControl {
    return this._control;
  }

  set formGroup(group: UntypedFormGroup) {
    this._group = group;
    group.addValidators((control) => {
      const path = this.getOwnerFieldId();
      const teamMemberControl = control.get(path);
      const additionalTeamMemberControl = control.get(this._fieldId());
      if (teamMemberControl && additionalTeamMemberControl) {
        const additionalTeamMemberValue: string[] = additionalTeamMemberControl.value;
        const teamMemberValue: string | SimplifiedUser = teamMemberControl.value;
        const userID = typeof teamMemberValue === 'string' ? teamMemberValue : teamMemberValue?.userId;
        if (teamMemberValue && additionalTeamMemberValue) {
          if (additionalTeamMemberValue.includes(userID)) {
            return {
              additionalTeamMemberIsOwner: true,
            };
          }
        }
      }
      return null;
    });
  }

  get formGroup(): UntypedFormGroup {
    return this._group;
  }

  _fieldId = signal('');
  set fieldId(fieldId: string) {
    this._fieldId.set(fieldId);
  }

  get hasModification(): boolean {
    return this._control.dirty;
  }

  set label(label: string) {
    this._label = label;
  }

  get label(): string {
    return this._label;
  }

  get value(): FieldValueInterface {
    return {
      fieldId: this._fieldId(),
      stringValues: {
        values: this._control.value,
      },
    };
  }

  loadingValues = signal(true);
  availableOptions = signal<SimplifiedUser[]>([]);
  filteredOptions: Signal<SimplifiedUser[]> = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.userId;
    return availableOptions.filter((tm) => userFilterFn(tm, filterTerm));
  });
  teamMembers = computed(() => {
    const availableOptions = this.availableOptions();
    const selectedTeamMemberIDs = this.selectedTeamMemberIDs();
    const teamMembers = [];
    for (const member of availableOptions) {
      if (selectedTeamMemberIDs.includes(member.userId)) {
        teamMembers.push(member);
      }
    }
    return teamMembers.length ? teamMembers : NO_VALUE_FOR_TEAM_MEMBER;
  });
  teamMemberNames = computed(() => {
    const teamMembers = this.teamMembers();
    if (teamMembers === NO_VALUE_FOR_TEAM_MEMBER) {
      return [];
    }
    return teamMembers.map((tm) => tm.displayName);
  });

  constructor(
    private readonly userService: CRMUserService,
    private readonly destroyRef: DestroyRef,
    private readonly inlineEditService: InlineEditService,
  ) {
    super();
    this.userService.searchUsers('').subscribe((allTeamMembers) => {
      this.availableOptions.set(allTeamMembers);
      this.loadingValues.set(false);
    });

    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        switchMap((searchValue) => {
          const filterTerm = typeof searchValue === 'string' ? searchValue : searchValue?.userId;
          return this.userService.searchUsers(filterTerm).pipe(
            map((results) => {
              return { searchValue, results };
            }),
          );
        }),
      )
      .subscribe((newValue) => {
        this.availableOptions.set(newValue.results);
        this.searchTerm.set(newValue.searchValue);
      });
  }

  displayFn(availableOptions: SimplifiedUser[]): (teamMember: SimplifiedUser | string) => string {
    return (teamMember: SimplifiedUser | string): string => {
      if (!teamMember) {
        return '';
      }
      if (typeof teamMember === 'string') {
        const found = availableOptions.find((tm) => tm.userId === teamMember);
        return found?.displayName || teamMember;
      }
      return teamMember?.displayName;
    };
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const currentTeamMember = this._control.value;
    const hasTeamMember = currentTeamMember.some((tm) => tm === event.option.value);
    if (event.option.value && !hasTeamMember) {
      this._control.patchValue([...currentTeamMember, event.option.value]);
      this._control.markAsDirty();
    }
    this.teamMemberInput.nativeElement.value = '';
    this.searchControl.setValue('');
  }

  add(event: MatChipInputEvent) {
    const currentTeamMember = this._control.value;
    const hasTeamMember = currentTeamMember.some((tm) => tm === event?.value);
    if (event?.value && !hasTeamMember) {
      // Even if we're not selecting from the available options, we still need to add the team member by ID
      const teamMemberID = this.getIDForTeamMember(event.value);
      if (teamMemberID) {
        this._control.patchValue([...currentTeamMember, teamMemberID]);
        this._control.markAsDirty();
      }
    }
    // Clear the input value
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    event.chipInput!.clear();
    this.searchControl.setValue('');
  }

  private getIDForTeamMember(teamMember: string) {
    const found = this.availableOptions().find(
      (tm) => tm.userId === teamMember || tm.displayName.toLowerCase() === teamMember.toLowerCase(),
    );
    return found?.userId;
  }

  remove(userId: string) {
    const currentTeamMember = this._control.value;
    const listWithoutSelected = currentTeamMember.filter((tm) => tm !== userId);
    this._control.patchValue([...listWithoutSelected]);
    this._control.markAsDirty();
  }

  saveField(): void {
    if (!this._control.errors && !this._group.errors?.additionalTeamMemberIsOwner) {
      // attempt saving owner field as well, if there are no changes it won't save
      const ownerFieldValue: FieldValueInterface = { fieldId: this.getOwnerFieldId() };
      const ownerFormControlValue: SimplifiedUser | string = this._group?.value[this.getOwnerFieldId()];
      if (typeof ownerFormControlValue === 'string') {
        ownerFieldValue.stringValue = ownerFormControlValue;
      } else {
        ownerFieldValue.stringValue = ownerFormControlValue?.userId;
      }
      this.inlineEditService.saveInlineEdit(this.objectType, [this.value, ownerFieldValue]);
    }
  }

  private getOwnerFieldId(): string {
    if (this.objectType === 'Contact') {
      return SystemFieldIds.ContactOwnerID;
    } else {
      return SystemFieldIds.CompanyOwnerID;
    }
  }
}
